import { roleAtom, userAtom, loadingAtom, availableRolesAtom } from "../state/"
import { useAtom } from "jotai"
import { useNavigate, useOutletContext } from "react-router-dom"
import { twJoin } from "tailwind-merge"
import Identity from "../Partials/Step/Identity"
import Gifts from "../Partials/Step/Gifts"
import Vision from "../Partials/Step/Vision"
import MentorCircle from "../Partials/Step/MentorCircle"
import Encouragement from "../Partials/Step/Encouragement"
import Commitments from "../Partials/Step/Commitments"
import Blessings from "../Partials/Step/Blessings"
import Modal from "./Modal"
import EditPlan from "../Partials/EditPlan"
import Invite from "../Partials/Invite"
import { LockedSection } from "./LockedSection"
import { useOutletContextPlan } from "../hooks/useOutletContextPlan"
import { PLAN_STATUS } from "../constants/planStatus"
import Spinner from "./Spinner"
import { PLAN_USER_ROLE, PLAN_USER_ROLE_LABELS } from "../constants/planUserRole"
import { usePlanRoles } from "../hooks/usePlanRoles"
import { NotAuthorizedComponent } from "../Pages/NotAuthorized"
import { useIsAdmin } from "../hooks/useIsAdmin"

function PlanContent() {
  const {
    plan,
    noPlans,
    editPlanOpen,
    setEditPlanOpen,
    onPlanEdit,
    onPlanDelete,
    inviteOpen,
    setInviteOpen,
    onDownload,
  } = useOutletContext()
  const isAdmin = useIsAdmin()
  const { currentStep, canInviteMentors, hasMentorContent } = useOutletContextPlan()
  const [role, setRole] = useAtom(roleAtom)
  const [availableRoles] = useAtom(availableRolesAtom)
  const [user] = useAtom(userAtom)

  const { isNotPlanMember, isMentor, isStudent, isGuide } = usePlanRoles()
  const [loading] = useAtom(loadingAtom)
  const navigate = useNavigate()

  const handleInviteMentors = () => {
    if (plan?.token) {
      navigate(`/plan/${plan.token}/checklist/invite-mentors`)
    }
  }

  const handleInviteStudent = () => {
    if (plan?.token) {
      navigate(`/plan/${plan.token}/checklist/invite-student`)
    }
  }

  if (loading || !plan) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner size={"lg"} />{" "}
      </div>
    )
  }

  if ((isNotPlanMember || (!isMentor && !isStudent && !isGuide)) && !isAdmin) {
    return <NotAuthorizedComponent />
  }

  return (
    <div className="flex min-h-screen flex-col p-6 md:p-8">
      <div className="flex flex-col rounded-md border bg-white p-6 md:flex-row md:items-center">
        <div className="mb-6 h-full border-neutral-200 py-2 md:mb-0 md:mr-6 md:border-r-2 md:pr-6">
          <img src="/logo.png" className="mx-auto w-[223px]" alt={"Encircled Logo"} />
        </div>
        <div className="flex flex-1 items-center justify-between gap-4 align-middle">
          <h1 className="flex gap-4 text-2xl font-bold">
            {plan && !loading ? plan.name : null}
            {(role === PLAN_USER_ROLE.YOUTH ||
              role === PLAN_USER_ROLE.GUIDE ||
              role === PLAN_USER_ROLE.INITIATOR ||
              isAdmin) &&
            !loading ? (
              <button
                className="-mt-1 block"
                onClick={() => {
                  setEditPlanOpen(true)
                }}
              >
                <i className="fa-solid fa-pen-to-square text-lg text-brand"></i>
              </button>
            ) : null}
          </h1>

          <div className="flex-grow"></div>

          {isAdmin && isNotPlanMember ? (
            <div className="rounded-md border-2 border-yellow-400 bg-yellow-50 px-4 py-3 text-sm">
              <div className="flex items-center gap-2">
                <i className="fa-solid fa-shield-halved text-yellow-600"></i>
                <span className="font-semibold text-yellow-800">Admin Mode:</span>
                <span className="text-yellow-700">
                  You're editing a plan you're not a member of
                </span>
              </div>
            </div>
          ) : null}

          <div className="flex gap-8">
            {user && plan && !loading && availableRoles.length > 1 ? (
              <div>
                Editing as
                <select
                  className="ml-2 w-28 rounded-md border border-neutral-300 p-2 pr-4"
                  value={role}
                  onChange={e => setRole(e.target.value)}
                >
                  {availableRoles
                    .filter(r =>
                      [PLAN_USER_ROLE.YOUTH, PLAN_USER_ROLE.MENTOR, PLAN_USER_ROLE.GUIDE].includes(
                        r,
                      ),
                    )
                    .map(role => (
                      <option key={role} value={role}>
                        {PLAN_USER_ROLE_LABELS[role]}
                      </option>
                    ))}
                </select>
              </div>
            ) : null}
          </div>

          <Modal isOpen={inviteOpen} setIsOpen={setInviteOpen} width="md:max-w-2xl">
            <Invite />
          </Modal>
          <Modal
            locked={noPlans}
            isOpen={editPlanOpen}
            setIsOpen={setEditPlanOpen}
            width="md:max-w-xl"
          >
            <EditPlan plan={plan} onEdit={onPlanEdit} onDelete={onPlanDelete} />
          </Modal>
        </div>
      </div>
      <LockedSection
        isLocked={
          currentStep === PLAN_STATUS.INVITE_STUDENT || currentStep === PLAN_STATUS.CHOOSE_GUIDE
        }
        lockText="Invite Student to Unlock"
        unlockAction={handleInviteStudent}
        unlockText="Invite Student"
      >
        <div className="grid flex-1 gap-3 pt-6 md:auto-rows-auto md:grid-cols-4 md:gap-4">
          <div className="my-4 flex items-center justify-center gap-2 md:hidden">
            <i
              className={twJoin(
                "fa-regular text-xl",
                role == PLAN_USER_ROLE.MENTOR
                  ? "fa-rocket text-neutral-400"
                  : "fa-pen-to-square text-brand",
              )}
            ></i>
            <div
              className={twJoin(
                "text-sm font-bold uppercase tracking-wide",
                role == PLAN_USER_ROLE.MENTOR ? "text-neutral-400" : "text-brand",
              )}
            >
              {role == PLAN_USER_ROLE.MENTOR
                ? "The student will complete these sections"
                : "You will complete these sections"}
            </div>
          </div>
          <div className="flex md:col-start-1 md:row-span-4 md:row-start-1">
            <Identity />
          </div>
          <div className="flex md:col-start-1 md:row-span-3 md:row-start-5">
            <Gifts />
          </div>
          <div className="flex md:col-start-2 md:row-span-4 md:row-start-1">
            <Vision />
          </div>
          <div className="flex md:col-start-2 md:row-span-3 md:row-start-5">
            <MentorCircle />
          </div>
          <div className="my-4 flex items-center justify-center gap-2 md:hidden">
            <i
              className={twJoin(
                "fa-regular text-xl",
                role == PLAN_USER_ROLE.YOUTH
                  ? "fa-rocket text-neutral-400"
                  : "fa-pen-to-square text-brand",
              )}
            ></i>
            <div
              className={twJoin(
                "text-sm font-bold uppercase tracking-wide",
                role == PLAN_USER_ROLE.YOUTH ? "text-neutral-400" : "text-brand",
              )}
            >
              {role == PLAN_USER_ROLE.MENTOR
                ? "You will complete these sections"
                : "Your mentors will complete these sections"}
            </div>
          </div>
          <LockedSection
            isLocked={
              (currentStep === PLAN_STATUS.INVITE_MENTORS ||
                currentStep === PLAN_STATUS.SIT_WITH_STUDENT) &&
              !hasMentorContent
            }
            gridArea="md:col-start-3 md:col-span-2 md:row-start-1 md:row-span-7"
            lockText={
              role === PLAN_USER_ROLE.YOUTH ? "Mentor Activities" : "Invite Mentors to Unlock"
            }
            unlockAction={role === PLAN_USER_ROLE.YOUTH ? undefined : () => handleInviteMentors()}
            unlockText={role === PLAN_USER_ROLE.YOUTH ? "" : "Invite Mentors"}
            canUnlock={canInviteMentors}
            tooltipText={
              role === PLAN_USER_ROLE.YOUTH
                ? canInviteMentors
                  ? "Unlocked when a mentor is invited"
                  : "Complete all sections to unlock"
                : canInviteMentors
                  ? undefined
                  : "Complete all sections to invite mentors"
            }
          >
            <div className="flex md:col-start-3 md:row-span-5 md:row-start-1">
              <Encouragement />
            </div>
            <div className="flex md:col-start-4 md:row-span-5 md:row-start-1">
              <Commitments />
            </div>
            <div className="flex md:col-span-2 md:col-start-3 md:row-span-2 md:row-start-6">
              <Blessings />
            </div>
          </LockedSection>
        </div>
      </LockedSection>
    </div>
  )
}

export default PlanContent
